import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { formatDate, formatMonth } from '@/utils/format';
import type { FacilityAgreement, Applicant, Consumer, ChangePlanRequest } from "../types";
import { InputFieldBox } from "@/components/InputField";
import { RadioFieldBox } from "@/components/RadioField";
import { DateFieldBox } from "@/components/DateField";
import InfoDisplay from "@/components/InfoDisplay";
import SidebarNavigation from "@/components/SidebarNavigation";
import Layout from "../../shared/components/Layout";
import AlertMessages from "@/components/AlertMessages";
import { getFormattedToday } from "@/utils/format";
import type { CustomerParams } from "../types";
import confirmCustomer from "../api/confirmCustomer";
import getCustomer from "../api/getCustomer";
import type { Alert } from "@/types";
import { ChangePlanForm } from "../components/ChangePlanForm";
import clearLocalStorage from "@/utils/clearLocalStorage";
import NotFound from "@/template/NotFound";

const Customer: React.FC = () => {
  const { custCode } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  const addMonths = (dateString: string, add: number): string => {
    const parts = dateString.split('-');
    let year = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);

    month += add;

    if (month > 12) {
        year += 1;
        month -= 12;
    }

    return `${year}/${month.toString().padStart(2, '0')}`;
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [alert, setAlert] = useState<Alert>({
    open: false,
    messages: [],
  })

  const [customer, setCustomer] = useState({
    custCode: "",
  });

  const [facilityAgreement, setFacilityAgreement] = useState<FacilityAgreement>();
  const [usageFeeOccurMonth, setUsageFeeOccurMonth] = useState<string>(addMonths(getFormattedToday(false), 2));
  const [usageFeeFirstPaymentMonth, setUsageFeeFirstPaymentMonth] = useState<string>(addMonths(getFormattedToday(false), 3));
  const [applicant, setApplicant] = useState<Applicant>({
    birthday: "",
    nameKana: "",
  });
  const [consumer, setConsumer] = useState<Consumer>({
    nameKana: "",
    relationship: "",
    isSameApplicant: true,
  });

  const[changePlanRequest, setChangePlanRequest] = useState<ChangePlanRequest>({
    afterPlan: "",
    changeMonth: "",
    isChangePlan: false,
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getCustomer(custCode);
        setCustomer(response.data.customer);
        setFacilityAgreement(response.data.facilityAgreement);
        setApplicant(response.data.applicant);
        setUsageFeeOccurMonth(addMonths(response.data.facilityAgreement.startMonth, 2));
        setUsageFeeFirstPaymentMonth(addMonths(response.data.facilityAgreement.startMonth, 3));
        setConsumer(prevConsumer => ({...prevConsumer, ...response.data.consumer}));

        const fromConfirmPage = location.state?.fromConfirmPage;
        if(fromConfirmPage) {
          const storedCustomer = localStorage.getItem("customer");
          if(storedCustomer) {
            setCustomer(JSON.parse(storedCustomer));
          }

          const storedApplicant = localStorage.getItem("applicant");
          if(storedApplicant) {
            setApplicant(JSON.parse(storedApplicant));
          }

          const storedConsumer = localStorage.getItem("consumer");
          if(storedConsumer) {
            setConsumer(JSON.parse(storedConsumer));
          }

          const storedFacilityAgreement = localStorage.getItem("facilityAgreement");
          if(storedFacilityAgreement) {
            setFacilityAgreement(JSON.parse(storedFacilityAgreement));
          }

          const storedChangePlanRequest = localStorage.getItem("changePlanRequest");
          if(storedChangePlanRequest) {
            setChangePlanRequest(JSON.parse(storedChangePlanRequest));
          }

          const storedUsageFeeOccurMonth = localStorage.getItem("usageFeeOccurMonth");
          if(storedUsageFeeOccurMonth) {
            setUsageFeeOccurMonth(JSON.parse(storedUsageFeeOccurMonth));
          }

          const storedUsageFeeFirstPaymentMonth = localStorage.getItem("usageFeeFirstPaymentMonth");
          if(storedUsageFeeFirstPaymentMonth) {
            setUsageFeeFirstPaymentMonth(JSON.parse(storedUsageFeeFirstPaymentMonth));
          }
        } else {
          clearLocalStorage();
        }
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        if (error.response && error.response.status === 404) {
          setIsNotFound(true);
        }
        console.error("Error fetching facility lens data:", error);
      }
    };

    fetchData();

  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let updatedConsumer = {...consumer};
    if (updatedConsumer.isSameApplicant) {
      updatedConsumer = {
        ...applicant,
        relationship: '本人',
        isSameApplicant: updatedConsumer.isSameApplicant,
      };
    }

    const params: CustomerParams = {
      changePlanRequest: changePlanRequest,
      applicant: applicant,
      consumer: updatedConsumer,
    }

    try {
      const res = await confirmCustomer(params, customer.custCode);
      setAlert({open: false, messages: []});
      localStorage.setItem("facilityAgreement", JSON.stringify(facilityAgreement));
      localStorage.setItem("changePlanRequest", JSON.stringify(changePlanRequest));
      localStorage.setItem("customer", JSON.stringify(customer));
      localStorage.setItem("applicant", JSON.stringify(applicant));
      localStorage.setItem("consumer", JSON.stringify(updatedConsumer));
      localStorage.setItem("usageFeeOccurMonth", JSON.stringify(usageFeeOccurMonth));
      localStorage.setItem("usageFeeFirstPaymentMonth", JSON.stringify(usageFeeFirstPaymentMonth));
      if (res.data.orderId){
        localStorage.setItem("orderId", JSON.stringify(res.data.orderId));
      } else {
        localStorage.removeItem("orderId");
      }

      navigate(`/facility/facility_agreements/customers/${customer.custCode}/confirm`);
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.errors) {
        setAlert({open: true, messages: err.response.data.errors.fullMessages});
        window.scrollTo(0, 0);
      } else {
        setAlert({open: false, messages: []});
        console.log('An unexpected error occurred:', err);
      }
    }
  }

  if (isLoading) {
    return <></>;
  }
  if (isNotFound) {
    return <NotFound />;
  }

  return (
    <Layout>
      <main className="wrapper" id="page-facility_login">
        <section className="sec-contact">
          <div className="inner">
            <h1 className="c-ttl__01">お客様登録情報変更入力</h1>
            <AlertMessages open={alert.open} messages={alert.messages} />
            <div className="contact-box">
              <div className="contact-box__left">
                <div className="mbpc-98 mbsp-32">
                  <form onSubmit={handleSubmit}>
                    <div className="contact-box__form js-item" id="id-01">
                      <h2 className="c-ttl__02">お客様情報</h2>
                      <InfoDisplay label="契約種別" value={"施設型定額制契約"} />
                      <InfoDisplay label="お客様ID" value={customer.custCode} />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-02">
                      <h2 className="c-ttl__02">申込者情報<span>※20歳未満の方は、保護者の方が申込者となります。</span></h2>
                      <InputFieldBox
                        label={"申込者名(カナ)"}
                        placeholder={"タナカ イチロウ"}
                        value={applicant.nameKana}
                        onChange={event => setApplicant({...applicant, nameKana: event.target.value })}
                        required={true}
                      />
                      <DateFieldBox
                        label={"申込者生年月日"}
                        pattern={"d{4}-d{2}-d{2}"}
                        value={applicant.birthday}
                        onChange={event => setApplicant({...applicant, birthday: event.target.value })}
                        required={true}
                      />
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                      <h2 className="c-ttl__02">レンズ使用者情報</h2>
                      <RadioFieldBox
                        label="使用者について"
                        list={[
                            { checked: consumer.isSameApplicant, label: "申込者と同一", onChange: () => setConsumer({...consumer, isSameApplicant: true}) },
                            { checked: !(consumer.isSameApplicant), label: "申込者と異なる", onChange: () => setConsumer({...consumer, isSameApplicant: false}) },
                          ]}
                        itemStart={false}
                      />
                      {!(consumer.isSameApplicant) &&
                        <>
                          <InputFieldBox
                            label={"使用者名(カナ)"}
                            placeholder={"タナカ イチロウ"}
                            value={consumer.nameKana}
                            onChange={event => setConsumer({...consumer, nameKana: event.target.value })}
                            required={true}
                          />
                          <InputFieldBox
                            label={"申込者との関係"}
                            placeholder={"子供"}
                            value={consumer.relationship}
                            onChange={event => setConsumer({...consumer, relationship: event.target.value })}
                            required={true}
                          />
                        </>
                      }
                    </div>
                    <div className="contact-box__form mbsp-29 js-item h-adr" id="id-03">
                      <h2 className="c-ttl__02">契約詳細</h2>
                      <ChangePlanForm
                        label="ご契約プラン"
                        status={facilityAgreement?.status}
                        plan={facilityAgreement?.plan}
                        changePlanRequest={changePlanRequest}
                        setChangePlanRequest={setChangePlanRequest}
                      />
                      <InfoDisplay label="サービス利用開始日" value={formatDate(facilityAgreement?.startDate ?? '')} />
                      <InfoDisplay label="サービス利用開始月" value={formatMonth(facilityAgreement?.startMonth ?? '')} />
                      <InfoDisplay label="利用料請求発生月" value={usageFeeOccurMonth} />
                      <InfoDisplay label="利用料初回決済月（月末）" value={usageFeeFirstPaymentMonth} />
                    </div>
                    <button type="submit" className="c-btn__01">入力内容の確認</button>
                  </form>
                </div>
              </div>
              <SidebarNavigation items={[
                { id: "id-01", label: "お客様情報" },
                { id: "id-02", label: "申込者情報" },
                { id: "id-03", label: "使用者情報" },
                { id: "id-04", label: "契約詳細" },
                ]} />
            </div>
          </div>
        </section>
      </main>
    </Layout>
  )
}

export default Customer;